import React from 'react';
import { motion } from 'framer-motion';
import {
  Inbox,
  Search,
  FileX,
  FolderOpen,
  Trash2,
  Mail,
  Calendar,
  Users,
  Image,
  Video,
  Music as MusicIcon,
  File,
  FileText,
  Award,
  Heart,
  Star,
  AlertTriangle,
  Info,
  CheckCircle,
  XCircle,
  Coffee as CoffeeIcon,
  Umbrella,
  Compass,
  Map,
  Package,
  ShoppingBag,
  CreditCard,
  Globe,
  Link2,
  Lock,
  Shield,
  Server,
  Database,
  Cloud as CloudIcon,
  Wifi,
  Battery,
  Thermometer,
  MapPin,
  Phone,
  MessageCircle,
  Send,
  Bell,
  Settings,
  HelpCircle,
  User,
  Users2,
  Home,
  Briefcase,
  DollarSign,
  TrendingUp,
  Activity,
  HeartPulse,
  Pill,
  Stethoscope,
  Bed,
  Car,
  Plane,
  Train,
  Ship,
  Bike,
  Footprints,
  Pizza,
  UtensilsCrossed,
  Cake,
  Soup,
  Salad,
  Apple,
  Carrot,
  Cookie,
  Wine,
  Beer,
  Coffee,
  Music,
  Droplet,
  Sun,
  Moon,
  Cloud,
  CloudRain,
  Snowflake,
  Flame,
  Sparkles,
  Zap,
  Wind,
  Plus,
  Upload,
  RefreshCw,
} from 'lucide-react';

export type EmptyStateVariant = 
  | 'default' | 'search' | 'folder' | 'inbox' 
  | 'error' | 'success' | 'warning' | 'info'
  | 'custom' | 'no-data' | 'no-results' | 'no-tasks'
  | 'calendar' | 'messages' | 'contacts' | 'files'
  | 'chart' | 'map' | 'video' | 'audio';

interface EmptyStateProps {
  variant?: EmptyStateVariant;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  illustration?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  children?: React.ReactNode;
  className?: string;
  compact?: boolean;
}

const variantConfig: Record<EmptyStateVariant, {
  icon: React.ElementType;
  title: string;
  description: string;
  gradient: string;
}> = {
  default: {
    icon: Inbox,
    title: 'No items yet',
    description: 'Get started by creating your first item',
    gradient: 'from-primary-500 to-info-500',
  },
  search: {
    icon: Search,
    title: 'No results found',
    description: 'Try adjusting your search or filters',
    gradient: 'from-surface-400 to-surface-500',
  },
  folder: {
    icon: FolderOpen,
    title: 'This folder is empty',
    description: 'Drag and drop files here or click to upload',
    gradient: 'from-info-500 to-primary-400',
  },
  inbox: {
    icon: Mail,
    title: 'Inbox zero',
    description: "You've got nothing here. Enjoy your peace!",
    gradient: 'from-success-500 to-info-400',
  },
  error: {
    icon: XCircle,
    title: 'Something went wrong',
    description: 'An error occurred. Please try again.',
    gradient: 'from-error-500 to-warning-400',
  },
  success: {
    icon: CheckCircle,
    title: 'All done!',
    description: "You've completed everything. Great job!",
    gradient: 'from-success-500 to-info-400',
  },
  warning: {
    icon: AlertTriangle,
    title: 'Warning',
    description: 'Please review the items below',
    gradient: 'from-warning-500 to-error-400',
  },
  info: {
    icon: Info,
    title: 'No information available',
    description: 'This section is currently empty',
    gradient: 'from-info-500 to-primary-500',
  },
  custom: {
    icon: Sparkles,
    title: 'Custom empty state',
    description: 'Create your own empty state message',
    gradient: 'from-primary-500 to-error-400',
  },
  'no-data': {
    icon: Database,
    title: 'No data yet',
    description: 'Data will appear here once collected',
    gradient: 'from-surface-500 to-surface-600',
  },
  'no-results': {
    icon: Search,
    title: 'No matches',
    description: 'Try different keywords or filters',
    gradient: 'from-surface-400 to-surface-500',
  },
  'no-tasks': {
    icon: CheckCircle,
    title: 'All tasks complete!',
    description: "You've finished everything on your list",
    gradient: 'from-success-500 to-info-500',
  },
  calendar: {
    icon: Calendar,
    title: 'No events scheduled',
    description: 'Add an event to see it here',
    gradient: 'from-info-500 to-primary-500',
  },
  messages: {
    icon: MessageCircle,
    title: 'No messages',
    description: 'Start a conversation',
    gradient: 'from-success-500 to-info-500',
  },
  contacts: {
    icon: Users,
    title: 'No contacts yet',
    description: 'Add your first contact',
    gradient: 'from-info-500 to-primary-500',
  },
  files: {
    icon: File,
    title: 'No files',
    description: 'Upload files to get started',
    gradient: 'from-warning-500 to-error-400',
  },
  chart: {
    icon: Activity,
    title: 'No data to display',
    description: 'Add data to see your chart',
    gradient: 'from-primary-500 to-error-400',
  },
  map: {
    icon: MapPin,
    title: 'No locations',
    description: 'Add locations to see them on the map',
    gradient: 'from-success-500 to-info-500',
  },
  video: {
    icon: Video,
    title: 'No videos',
    description: 'Upload a video to get started',
    gradient: 'from-error-500 to-warning-400',
  },
  audio: {
    icon: Music,
    title: 'No audio files',
    description: 'Add audio files to see them here',
    gradient: 'from-primary-500 to-info-500',
  },
};

export function EmptyState({
  variant = 'default',
  title,
  description,
  icon,
  illustration,
  action,
  secondaryAction,
  children,
  className = '',
  compact = false,
}: EmptyStateProps) {
  const config = variantConfig[variant];
  const Icon = icon || config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className={`flex flex-col items-center justify-center text-center ${
        compact ? 'py-8 px-4' : 'py-12 px-6'
      } ${className}`}
    >
      {/* Icon/Illustration Container */}
      <div className={`relative mb-6 ${
        compact ? 'w-16 h-16' : 'w-24 h-24'
      }`}>
        {/* Background glow */}
        <div className={`
          absolute inset-0 rounded-full blur-xl opacity-50
          bg-gradient-to-br ${config.gradient}
        `} />
        
        {/* Icon circle */}
        <div className={`
          relative w-full h-full rounded-full
          bg-gradient-to-br ${config.gradient}
          flex items-center justify-center
          shadow-lg
          ${compact ? 'shadow-md' : 'shadow-xl'}
        `}>
          {illustration ? (
            illustration
          ) : (
            <Icon className={`${
              compact ? 'w-8 h-8' : 'w-12 h-12'
            } text-white`} />
          )}
        </div>

        {/* Decorative elements */}
        {!compact && (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-khiimori-bg-elevated dark:bg-khiimori-bg-elevated shadow-md flex items-center justify-center"
            >
              <Sparkles className="w-4 h-4 text-primary-500" />
            </motion.div>
            <motion.div
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute -bottom-1 -left-3 w-6 h-6 rounded-full bg-khiimori-bg-elevated dark:bg-khiimori-bg-elevated shadow-sm flex items-center justify-center"
            >
              <Star className="w-3 h-3 text-warning-500 fill-current" />
            </motion.div>
          </>
        )}
      </div>

      {/* Title */}
      <h3 className={`font-semibold text-khiimori-text-secondary dark:text-khiimori-text-primary ${
        compact ? 'text-base mb-1' : 'text-xl mb-2'
      }`}>
        {title || config.title}
      </h3>

      {/* Description */}
      <p className={`text-khiimori-text-muted dark:text-khiimori-text-muted max-w-sm ${
        compact ? 'text-sm' : 'text-base'
      }`}>
        {description || config.description}
      </p>

      {/* Children (custom content) */}
      {children && (
        <div className="mt-4 w-full max-w-xs">
          {children}
        </div>
      )}

      {/* Actions */}
      {(action || secondaryAction) && (
        <div className={`flex items-center gap-3 mt-6 ${
          action && secondaryAction ? 'flex-row' : 'flex-row'
        }`}>
          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              className={`
                px-4 py-2 text-sm font-medium rounded-lg
                bg-khiimori-bg-elevated dark:bg-khiimori-bg-elevated
                text-khiimori-text-secondary
                hover:bg-surface-200 dark:hover:bg-khiimori-bg-tertiary
                transition-colors
              `}
            >
              {secondaryAction.label}
            </button>
          )}
          {action && (
            <button
              onClick={action.onClick}
              className={`
                flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg
                bg-gradient-to-r ${config.gradient}
                text-khiimori-text-primary shadow-md
                hover:shadow-lg hover:scale-105
                transition-all
              `}
            >
              {action.icon && <span className="w-4 h-4">{action.icon}</span>}
              {action.label}
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
}

// Preset Empty State Components
export function NoDataState({ 
  onAction, 
  actionLabel = 'Add Data',
  title = 'No data yet',
}: {
  onAction?: () => void;
  actionLabel?: string;
  title?: string;
}) {
  return (
    <EmptyState
      variant="no-data"
      title={title}
      description="Start by adding your first data entry"
      action={onAction ? { label: actionLabel, onClick: onAction, icon: <Plus className="w-4 h-4" /> } : undefined}
    />
  );
}

export function NoResultsState({ 
  onClearFilters,
  query,
}: {
  onClearFilters?: () => void;
  query?: string;
}) {
  return (
    <EmptyState
      variant="search"
      title={query ? `No results for "${query}"` : 'No results found'}
      description="Try adjusting your search terms or filters"
      action={onClearFilters ? { label: 'Clear Filters', onClick: onClearFilters } : undefined}
    />
  );
}

export function ErrorState({ 
  onRetry, 
  error,
}: {
  onRetry?: () => void;
  error?: string;
}) {
  return (
    <EmptyState
      variant="error"
      title="Something went wrong"
      description={error || 'An unexpected error occurred. Please try again.'}
      action={onRetry ? { label: 'Try Again', onClick: onRetry, icon: <RefreshCw className="w-4 h-4" /> } : undefined}
    />
  );
}

export function SuccessState({ 
  message = 'Success!',
  action,
}: {
  message?: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <EmptyState
      variant="success"
      title={message}
      description="The operation completed successfully"
      action={action}
    />
  );
}

// Inbox Empty State
export function InboxEmptyState({ onCompose }: { onCompose?: () => void }) {
  return (
    <EmptyState
      variant="inbox"
      title="Inbox Zero"
      description="You've achieved inbox emptiness. Enjoy your day!"
      action={onCompose ? { label: 'Compose', onClick: onCompose } : undefined}
    />
  );
}

// Task Board Empty State
export function TaskBoardEmptyState({ onAddTask }: { onAddTask?: () => void }) {
  return (
    <EmptyState
      variant="no-tasks"
      title="No tasks yet"
      description="Create your first task to get started"
      action={onAddTask ? { label: 'Add Task', onClick: onAddTask, icon: <Plus className="w-4 h-4" /> } : undefined}
    />
  );
}

// File Manager Empty State
export function FileManagerEmptyState({ onUpload }: { onUpload?: () => void }) {
  return (
    <EmptyState
      variant="folder"
      title="Folder is empty"
      description="Drag and drop files here or click to upload"
      action={onUpload ? { label: 'Upload Files', onClick: onUpload, icon: <Upload className="w-4 h-4" /> } : undefined}
    />
  );
}

// No Messages Empty State
export function NoMessagesState({ onNewMessage }: { onNewMessage?: () => void }) {
  return (
    <EmptyState
      variant="messages"
      title="No messages yet"
      description="Start a conversation with someone"
      action={onNewMessage ? { label: 'New Message', onClick: onNewMessage, icon: <MessageCircle className="w-4 h-4" /> } : undefined}
    />
  );
}

// No Events Empty State
export function NoEventsState({ onAddEvent }: { onAddEvent?: () => void }) {
  return (
    <EmptyState
      variant="calendar"
      title="No events today"
      description="Your schedule is clear. Add an event?"
      action={onAddEvent ? { label: 'Add Event', onClick: onAddEvent, icon: <Plus className="w-4 h-4" /> } : undefined}
    />
  );
}

// Compact inline version
export function EmptyInline({ 
  icon: Icon = Inbox,
  text = 'No items',
}: {
  icon?: React.ElementType;
  text?: string;
}) {
  return (
    <div className="flex items-center gap-2 py-4 text-khiimori-text-muted">
      <Icon className="w-4 h-4" />
      <span className="text-sm">{text}</span>
    </div>
  );
}

export default EmptyState;